const Y_AXIS_MIN = 0;
const Y_AXIS_MAX = 100;

/** Preserve zero scores; only null/invalid values are omitted from the series. */
export function normalizeTrendScore(value: number | null | undefined): number | null {
  if (value == null || !Number.isFinite(value)) return null;
  return value;
}

/** Align a trend series to a canonical bucket list (e.g. org vs segment chart buckets). */
export function alignSeriesToBuckets(
  targetBuckets: string[],
  sourceBuckets: string[],
  series: Array<number | null>,
): Array<number | null> {
  if (targetBuckets.length === 0) return [];
  if (sourceBuckets.length === targetBuckets.length && sourceBuckets.every((b, i) => b === targetBuckets[i])) {
    return series;
  }

  const byBucket = new Map(sourceBuckets.map((bucket, index) => [bucket, series[index] ?? null]));
  return targetBuckets.map((bucket) => byBucket.get(bucket) ?? null);
}

export type YAxisBounds = {
  min: number;
  max: number;
  stepSize: number;
};

export function niceStepSize(rawStep: number): number {
  if (!Number.isFinite(rawStep) || rawStep <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(rawStep));
  const residual = rawStep / magnitude;
  if (residual <= 1) return magnitude;
  if (residual <= 2) return 2 * magnitude;
  if (residual <= 5) return 5 * magnitude;
  return 10 * magnitude;
}

/** Decimals worth keeping for a tick of this step, with two digits of headroom. */
export function stepDecimals(stepSize: number): number {
  if (!Number.isFinite(stepSize) || stepSize <= 0) return 2;
  return Math.min(12, Math.max(0, Math.ceil(-Math.log10(stepSize)) + 2));
}

/** Clears the floating-point residue left by stepping an axis in fractional increments. */
export function roundToStep(value: number, stepSize: number): number {
  return Number(value.toFixed(stepDecimals(stepSize)));
}

type PaddedYAxisBoundsOptions = {
  clampMin?: number;
  clampMax?: number;
};

/**
 * Dynamic y-axis: focus on data range (min–max) with padding and readable tick steps.
 * Defaults clamp to 0–100 for proficiency scores; pass unbounded clampMax for currency axes.
 */
export function computePaddedYAxisBounds(values: number[], options?: PaddedYAxisBoundsOptions): YAxisBounds {
  const clampMin = options?.clampMin ?? Y_AXIS_MIN;
  const clampMax = options?.clampMax ?? Y_AXIS_MAX;

  const finite = values.filter((value) => Number.isFinite(value));
  if (finite.length === 0) {
    return { min: clampMin, max: clampMax === Y_AXIS_MAX ? Y_AXIS_MAX : clampMax, stepSize: 10 };
  }

  const forRange = finite.filter((value) => value !== 0);
  const rangeValues = forRange.length > 0 ? forRange : finite;
  const dataMin = Math.min(...rangeValues);
  const dataMax = Math.max(...rangeValues);
  const span = dataMax - dataMin;

  if (span === 0) {
    const stepSize = clampMax === Y_AXIS_MAX ? 5 : niceStepSize(Math.max(dataMax * 0.2, 1));
    const min = Math.max(clampMin, Math.floor((dataMin - stepSize) / stepSize) * stepSize);
    const max = Math.min(clampMax, Math.ceil((dataMax + stepSize) / stepSize) * stepSize);
    return { min, max: Math.max(max, min + stepSize * 2), stepSize };
  }

  const padding = Math.max(span * 0.15, clampMax === Y_AXIS_MAX ? 2 : span * 0.1);

  let min = dataMin - padding;
  let max = dataMax + padding;

  let stepSize = niceStepSize((max - min) / 4);
  if (span <= 10 && stepSize < 5 && clampMax === Y_AXIS_MAX) {
    stepSize = 5;
  }

  min = Math.floor(min / stepSize) * stepSize;
  max = Math.ceil(max / stepSize) * stepSize;

  if (max - min < stepSize * 2) {
    const mid = (dataMin + dataMax) / 2;
    min = Math.floor((mid - stepSize) / stepSize) * stepSize;
    max = Math.ceil((mid + stepSize) / stepSize) * stepSize;
  }

  min = Math.max(clampMin, min);
  max = Math.min(clampMax, max);

  if (min >= max) {
    max = Math.min(clampMax, min + stepSize * 2);
  }

  return { min, max, stepSize };
}

/**
 * Tighter axis for the segment proficiency scale: hugs the data min/max so clustered
 * segments spread across the plot instead of bunching in the middle.
 */
export function computeSegmentScaleAxisBounds(values: number[]): YAxisBounds {
  const finite = values.filter((value) => Number.isFinite(value));
  if (finite.length === 0) {
    return { min: Y_AXIS_MIN, max: Y_AXIS_MAX, stepSize: 10 };
  }

  const dataMin = Math.min(...finite);
  const dataMax = Math.max(...finite);
  const span = dataMax - dataMin;

  if (span === 0) {
    const stepSize = 1;
    const min = Math.max(Y_AXIS_MIN, dataMin - stepSize);
    const max = Math.min(Y_AXIS_MAX, Math.max(dataMax + stepSize, min + stepSize * 2));
    return { min, max, stepSize };
  }

  const padding = Math.max(span * 0.08, 0.5);
  let min = dataMin - padding;
  let max = dataMax + padding;

  let stepSize = niceStepSize((max - min) / 5);
  if (span <= 3) {
    stepSize = 0.5;
  } else if (span <= 8) {
    stepSize = Math.min(stepSize, 1);
  } else if (span <= 15) {
    stepSize = Math.min(stepSize, 2);
  }

  min = Math.floor(min / stepSize) * stepSize;
  max = Math.ceil(max / stepSize) * stepSize;

  min = Math.max(Y_AXIS_MIN, min);
  max = Math.min(Y_AXIS_MAX, max);

  if (min >= max) {
    max = Math.min(Y_AXIS_MAX, min + stepSize * 2);
  }

  return { min, max, stepSize };
}

/**
 * Scale axis for unbounded positive metrics such as spend per task: same tight framing as
 * `computeSegmentScaleAxisBounds`, without the 0-100 clamp and the score-sized tick floors.
 */
export function computeSpendScaleAxisBounds(values: number[]): YAxisBounds {
  const finite = values.filter((value) => Number.isFinite(value) && value >= 0);
  if (finite.length === 0) {
    return { min: 0, max: 1, stepSize: 0.25 };
  }

  const dataMin = Math.min(...finite);
  const dataMax = Math.max(...finite);
  const span = dataMax - dataMin;

  if (span === 0) {
    const stepSize = niceStepSize(dataMax > 0 ? dataMax / 2 : 1);
    const min = Math.max(0, Math.floor((dataMin - stepSize) / stepSize) * stepSize);
    return { min: roundToStep(min, stepSize), max: roundToStep(min + stepSize * 2, stepSize), stepSize };
  }

  const padding = span * 0.08;
  const stepSize = niceStepSize((span + padding * 2) / 5);

  const min = Math.max(0, Math.floor((dataMin - padding) / stepSize) * stepSize);
  const max = Math.max(Math.ceil((dataMax + padding) / stepSize) * stepSize, min + stepSize * 2);

  return { min: roundToStep(min, stepSize), max: roundToStep(max, stepSize), stepSize };
}
